import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Alert, Chip, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Skeleton, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tabs, Tab, Divider, LinearProgress, Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EmailIcon from '@mui/icons-material/Email';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Layout from '../components/Layout';
import {
  uploadInvoice, fetchInvoices, fetchAlerts, fetchInvoiceStats,
  updateAlertStatus, generateAlertWhatsApp, fetchArticleHistory,
  fetchMailboxes, createMailbox, updateMailbox, deleteMailbox,
  pollMailboxNow, pollAllMailboxes, fetchIncomingEmails,
  InvoiceSummary, PriceAlert, InvoiceStats,
  Mailbox, IncomingEmail, MailboxIn, apiErrorText } from '../api/client';

function fmtEUR(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; }
}

export default function RechnungenPage() {
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState('');

  const [waOpen, setWaOpen] = useState(false);
  const [waAlert, setWaAlert] = useState<PriceAlert | null>(null);
  const [waPhone, setWaPhone] = useState('');
  const [waMessage, setWaMessage] = useState('');

  const [histOpen, setHistOpen] = useState(false);
  const [histData, setHistData] = useState<any>(null);

  // Mailbox state
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [incoming, setIncoming] = useState<IncomingEmail[]>([]);
  const [mbDlgOpen, setMbDlgOpen] = useState(false);
  const [mbEditing, setMbEditing] = useState<Mailbox | null>(null);
  const [mbForm, setMbForm] = useState<MailboxIn>({
    name: '', host: '', port: 993, use_ssl: true,
    username: '', password: '', folder: 'INBOX',
    poll_interval_min: 15, is_active: true,
  });

  const isAdmin = (localStorage.getItem('role') || '') === 'admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, inv, al] = await Promise.all([
        fetchInvoiceStats(),
        fetchInvoices(100),
        fetchAlerts('open', 200),
      ]);
      setStats(s);
      setInvoices(inv);
      setAlerts(al);
      if (isAdmin) {
        try {
          const [mb, ie] = await Promise.all([fetchMailboxes(), fetchIncomingEmails(100)]);
          setMailboxes(mb);
          setIncoming(ie);
        } catch { /* non-admin or no mailbox configured */ }
      }
    } catch (e) {
      setSnack('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const openMailboxDialog = (mb?: Mailbox) => {
    if (mb) {
      setMbEditing(mb);
      setMbForm({
        name: mb.name, host: mb.host, port: mb.port, use_ssl: mb.use_ssl,
        username: mb.username, password: '', folder: mb.folder,
        poll_interval_min: mb.poll_interval_min, is_active: mb.is_active,
      });
    } else {
      setMbEditing(null);
      setMbForm({
        name: '', host: '', port: 993, use_ssl: true,
        username: '', password: '', folder: 'INBOX',
        poll_interval_min: 15, is_active: true,
      });
    }
    setMbDlgOpen(true);
  };

  const saveMailbox = async () => {
    try {
      if (mbEditing) {
        await updateMailbox(mbEditing.id, mbForm);
        setSnack('Postfach aktualisiert');
      } else {
        await createMailbox(mbForm);
        setSnack('Postfach hinzugefügt');
      }
      setMbDlgOpen(false);
      await refresh();
    } catch (e: any) {
      setSnack(apiErrorText(e) || 'Speichern fehlgeschlagen');
    }
  };

  const removeMailbox = async (mb: Mailbox) => {
    if (!window.confirm(`Postfach "${mb.name}" wirklich löschen?`)) return;
    await deleteMailbox(mb.id);
    await refresh();
  };

  const pollNow = async (mb?: Mailbox) => {
    try {
      if (mb) {
        await pollMailboxNow(mb.id);
        setSnack(`Polling für "${mb.name}" gestartet`);
      } else {
        await pollAllMailboxes();
        setSnack('Polling für alle Postfächer gestartet');
      }
      // results trickle in async; refresh after a beat
      setTimeout(refresh, 4000);
    } catch (e: any) {
      setSnack(apiErrorText(e) || 'Polling fehlgeschlagen');
    }
  };

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const res = await uploadInvoice(file);
      const newCount = (res.new_alerts || []).length;
      setSnack(`Rechnung gespeichert. ${newCount} Preiswarnungen.`);
      await refresh();
    } catch (e: any) {
      setSnack(apiErrorText(e) || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleDropFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(handleUpload);
  };

  const dismissAlert = async (a: PriceAlert) => {
    if (!a.id) return;
    await updateAlertStatus(a.id, 'dismissed');
    setAlerts(prev => prev.filter(x => x.id !== a.id));
  };

  const resolveAlert = async (a: PriceAlert) => {
    if (!a.id) return;
    await updateAlertStatus(a.id, 'resolved');
    setAlerts(prev => prev.filter(x => x.id !== a.id));
  };

  const openWhatsApp = async (a: PriceAlert) => {
    if (!a.id) return;
    setWaAlert(a);
    const res = await generateAlertWhatsApp(a.id, '');
    setWaMessage(res.message);
    setWaPhone('');
    setWaOpen(true);
  };

  const sendWhatsApp = async () => {
    if (!waAlert?.id) return;
    const res = await generateAlertWhatsApp(waAlert.id, waPhone);
    window.open(res.phone_url, '_blank');
    setWaOpen(false);
  };

  const showHistory = async (supplier: string, articleNo: string) => {
    try {
      const data = await fetchArticleHistory(supplier, articleNo);
      setHistData(data);
      setHistOpen(true);
    } catch {
      setSnack('Keine Historie verfügbar');
    }
  };

  // Group alerts by (supplier, article_no) — one card per article, not per invoice line.
  // Yearly impact: the largest single projection (each underlying row already projects a
  // full-year cost; summing them would triple-count). Average delta across rows.
  type AlertGroup = {
    supplier: string;
    article_no: string;
    article_name: string;
    occurrences: number;
    avg_delta_pct: number;
    yearly_impact_eur: number;
    latest: PriceAlert;
    underlying: PriceAlert[];
  };
  const alertGroups: AlertGroup[] = React.useMemo(() => {
    const byKey = new Map<string, AlertGroup>();
    for (const a of alerts) {
      const key = `${a.supplier}::${a.article_no}`;
      const prev = byKey.get(key);
      if (!prev) {
        byKey.set(key, {
          supplier: a.supplier,
          article_no: a.article_no,
          article_name: a.article_name,
          occurrences: 1,
          avg_delta_pct: a.delta_pct,
          yearly_impact_eur: a.yearly_impact_eur,
          latest: a,
          underlying: [a],
        });
      } else {
        prev.occurrences += 1;
        prev.avg_delta_pct = (prev.avg_delta_pct * (prev.occurrences - 1) + a.delta_pct) / prev.occurrences;
        prev.yearly_impact_eur = Math.max(prev.yearly_impact_eur, a.yearly_impact_eur);
        prev.underlying.push(a);
        // keep "latest" = the one with the most recent invoice_date
        if ((a.invoice_date || '') > (prev.latest.invoice_date || '')) {
          prev.latest = a;
        }
      }
    }
    return Array.from(byKey.values()).sort((a, b) => b.yearly_impact_eur - a.yearly_impact_eur);
  }, [alerts]);
  // Headline savings = cross-supplier only ("buy it where it's cheapest");
  // same-supplier creep is negotiation leverage, not a switchable saving.
  // Matches the Dashboard KPI and the recompute endpoint.
  const dedupSavings = alertGroups.reduce((s, g) => {
    const cross = g.underlying.filter(a => a.alert_type !== 'same_supplier');
    return s + (cross.length ? Math.max(...cross.map(a => a.yearly_impact_eur || 0)) : 0);
  }, 0);

  const resolveGroup = async (g: AlertGroup) => {
    for (const a of g.underlying) {
      if (a.id) await updateAlertStatus(a.id, 'resolved');
    }
    setAlerts(prev => prev.filter(x => !g.underlying.some(u => u.id === x.id)));
  };
  const dismissGroup = async (g: AlertGroup) => {
    for (const a of g.underlying) {
      if (a.id) await updateAlertStatus(a.id, 'dismissed');
    }
    setAlerts(prev => prev.filter(x => !g.underlying.some(u => u.id === x.id)));
  };

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Rechnungen
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Alle Rechnungen mit Original-PDF, Upload und Posteingang. Preiswarnungen findest du im Dashboard.
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card><CardContent>
          <Typography variant="caption" color="text.secondary">Rechnungen</Typography>
          <Typography variant="h5" fontWeight={700}>{stats?.invoice_count ?? '—'}</Typography>
        </CardContent></Card>
        <Card><CardContent>
          <Typography variant="caption" color="text.secondary">Artikel in Datenbank</Typography>
          <Typography variant="h5" fontWeight={700}>{stats?.article_count ?? '—'}</Typography>
        </CardContent></Card>
        <Card><CardContent>
          <Typography variant="caption" color="text.secondary">Offene Warnungen</Typography>
          <Typography variant="h5" fontWeight={700} color={stats?.open_alerts ? 'error.main' : 'success.main'}>
            {stats?.open_alerts ?? '—'}
          </Typography>
        </CardContent></Card>
        <Card><CardContent>
          <Typography variant="caption" color="text.secondary">Sparpotenzial / Jahr</Typography>
          <Typography variant="h5" fontWeight={700} color="success.main">
            {fmtEUR(dedupSavings)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {alertGroups.length} Artikel betroffen
          </Typography>
        </CardContent></Card>
      </Box>

      {/* Upload */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); handleDropFiles(e.dataTransfer.files); }}
            sx={{
              border: '2px dashed', borderColor: 'divider', borderRadius: 2,
              p: 4, textAlign: 'center', bgcolor: 'grey.50',
            }}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" fontWeight={500}>
              PDF-Rechnung hier ablegen oder Datei auswählen
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Unterstützt: FrischeParadies, Georg Wagner, Schloss Filseck-Lieferanten
            </Typography>
            <Button
              variant="contained"
              component="label"
              disabled={uploading}
              startIcon={<UploadFileIcon />}
            >
              {uploading ? 'Lade hoch...' : 'Datei auswählen'}
              <input hidden type="file" accept="application/pdf" multiple
                     onChange={(e) => handleDropFiles(e.target.files)} />
            </Button>
            {uploading && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<ReceiptLongIcon />} iconPosition="start" label={`Rechnungen (${invoices.length})`} />
        {isAdmin && <Tab icon={<EmailIcon />} iconPosition="start" label={`Posteingang (${incoming.length})`} />}
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Lieferant</TableCell>
                <TableCell>Rechnung Nr.</TableCell>
                <TableCell align="right">Positionen</TableCell>
                <TableCell align="right">Summe netto</TableCell>
                <TableCell align="center">Warnungen</TableCell>
                <TableCell>Datei</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map(inv => (
                <TableRow key={inv.id} hover>
                  <TableCell>{fmtDate(inv.invoice_date)}</TableCell>
                  <TableCell>{inv.supplier}</TableCell>
                  <TableCell>{inv.invoice_no || '—'}</TableCell>
                  <TableCell align="right">{inv.line_count}</TableCell>
                  <TableCell align="right">{fmtEUR(inv.total_net)}</TableCell>
                  <TableCell align="center">
                    {inv.alert_count > 0 ? (
                      <Chip size="small" color="warning" label={inv.alert_count} />
                    ) : '—'}
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{inv.source_file}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && isAdmin && (
        <Box>
          {/* Mailboxes config */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>E-Mail-Postfächer</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Rechnungs-E-Mails werden alle paar Minuten automatisch geholt und Anhänge ausgelesen.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" startIcon={<SyncIcon />} onClick={() => pollNow()}>
                    Alle abfragen
                  </Button>
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => openMailboxDialog()}>
                    Postfach
                  </Button>
                </Stack>
              </Box>
              {mailboxes.length === 0 ? (
                <Alert severity="info">
                  Kein Postfach konfiguriert. Tipp: leite Lieferanten-Rechnungen an z. B.
                  <strong> rechnungen@vujicic-gastro.de </strong> weiter und richte hier den IMAP-Zugang ein.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Host</TableCell>
                        <TableCell>Benutzer</TableCell>
                        <TableCell align="center">Intervall</TableCell>
                        <TableCell align="center">Aktiv</TableCell>
                        <TableCell>Letzter Abruf</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mailboxes.map(mb => (
                        <TableRow key={mb.id} hover>
                          <TableCell>{mb.name}</TableCell>
                          <TableCell><Typography variant="caption">{mb.host}:{mb.port}{mb.use_ssl ? ' (SSL)' : ''}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{mb.username}</Typography></TableCell>
                          <TableCell align="center">{mb.poll_interval_min} min</TableCell>
                          <TableCell align="center">
                            {mb.is_active ? <Chip size="small" color="success" label="aktiv" /> : <Chip size="small" label="aus" />}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {mb.last_polled_at ? fmtDate(mb.last_polled_at) : '—'}
                              {mb.last_error && <span style={{ color: '#d32f2f' }}> · Fehler</span>}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Jetzt abfragen">
                              <IconButton size="small" onClick={() => pollNow(mb)}><SyncIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Button size="small" onClick={() => openMailboxDialog(mb)}>Bearbeiten</Button>
                            <IconButton size="small" onClick={() => removeMailbox(mb)}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Incoming emails log */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Eingegangene E-Mails</Typography>
              {incoming.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Noch keine E-Mails verarbeitet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Empfangen</TableCell>
                        <TableCell>Von</TableCell>
                        <TableCell>Betreff</TableCell>
                        <TableCell align="center">Anhänge</TableCell>
                        <TableCell align="center">Verarbeitet</TableCell>
                        <TableCell align="center">Übersprungen</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incoming.map(e => (
                        <TableRow key={e.id} hover>
                          <TableCell>{fmtDate(e.received_at) || fmtDate(e.created_at)}</TableCell>
                          <TableCell><Typography variant="caption">{e.from_addr}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{e.subject}</Typography></TableCell>
                          <TableCell align="center">{e.attachment_count}</TableCell>
                          <TableCell align="center">{e.processed_count}</TableCell>
                          <TableCell align="center">{e.skipped_count || '—'}</TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              color={e.status === 'done' ? 'success' : e.status === 'error' ? 'error' : 'default'}
                              label={e.status}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Mailbox config dialog */}
      <Dialog open={mbDlgOpen} onClose={() => setMbDlgOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{mbEditing ? 'Postfach bearbeiten' : 'Postfach hinzufügen'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name (z. B. Rechnungen Plenum)" size="small" fullWidth
              value={mbForm.name} onChange={e => setMbForm({...mbForm, name: e.target.value})} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 1 }}>
              <TextField label="IMAP Host" size="small"
                value={mbForm.host} onChange={e => setMbForm({...mbForm, host: e.target.value})} />
              <TextField label="Port" size="small" type="number"
                value={mbForm.port} onChange={e => setMbForm({...mbForm, port: parseInt(e.target.value || '993')})} />
              <TextField label="SSL" size="small" select SelectProps={{ native: true }}
                value={mbForm.use_ssl ? '1' : '0'}
                onChange={e => setMbForm({...mbForm, use_ssl: e.target.value === '1'})}>
                <option value="1">Ja</option>
                <option value="0">Nein</option>
              </TextField>
            </Box>
            <TextField label="Benutzername" size="small" fullWidth
              value={mbForm.username} onChange={e => setMbForm({...mbForm, username: e.target.value})} />
            <TextField label={mbEditing ? 'Passwort (leer = unverändert)' : 'Passwort'}
              type="password" size="small" fullWidth
              value={mbForm.password || ''} onChange={e => setMbForm({...mbForm, password: e.target.value})} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
              <TextField label="Ordner" size="small"
                value={mbForm.folder} onChange={e => setMbForm({...mbForm, folder: e.target.value})} />
              <TextField label="Intervall (min)" size="small" type="number"
                value={mbForm.poll_interval_min}
                onChange={e => setMbForm({...mbForm, poll_interval_min: parseInt(e.target.value || '15')})} />
              <TextField label="Aktiv" size="small" select SelectProps={{ native: true }}
                value={mbForm.is_active ? '1' : '0'}
                onChange={e => setMbForm({...mbForm, is_active: e.target.value === '1'})}>
                <option value="1">Ja</option>
                <option value="0">Nein</option>
              </TextField>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Tipp Gmail/IONOS: SSL=Ja, Port 993. Für Gmail ein App-Passwort verwenden.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMbDlgOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={saveMailbox}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp dialog */}
      <Dialog open={waOpen} onClose={() => setWaOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>WhatsApp-Nachricht an Lieferant</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" label="Lieferanten-Telefon (international, z. B. 4915112345678)"
            value={waPhone} onChange={e => setWaPhone(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <Divider sx={{ mb: 2 }} />
          <TextField
            fullWidth multiline minRows={10} value={waMessage}
            onChange={e => setWaMessage(e.target.value)}
            label="Nachricht (bearbeitbar)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="success" startIcon={<WhatsAppIcon />} onClick={sendWhatsApp}>
            In WhatsApp öffnen
          </Button>
        </DialogActions>
      </Dialog>

      {/* History dialog */}
      <Dialog open={histOpen} onClose={() => setHistOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Preisverlauf — {histData?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {histData?.supplier} · Art.-Nr. {histData?.article_no} · Einheit {histData?.unit}
          </Typography>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell align="right">Preis/Einheit</TableCell>
                <TableCell align="right">Menge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(histData?.points || []).map((p: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{fmtDate(p.date)}</TableCell>
                  <TableCell align="right">{fmtEUR(p.unit_price)}</TableCell>
                  <TableCell align="right">{p.quantity}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack('')} message={snack} />
    </Layout>
  );
}
